import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import translate from "@insite/client-framework/Translate";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadDefaultWarehouse from "@tggstore/Store/Handlers/Custom/LoadWarehouse";
import loadWarehouseList from "@tggstore/Store/Handlers/Custom/LoadWarehouses";
import updateDefaultWarehouse from "@tggstore/Store/Handlers/Custom/UpdateDefaultWarehouse";
import { CustomState } from "@tggstore/Store/Reducers";
import { colorStrings } from "@tggstore/Utilities/variables";
import { commonStylesHeaderTypography } from "@tggstore/WidgetExtensions/Header/SecondaryNavigationExtension";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isMobile: boolean;
}

export interface WarehouseSelectorStyles {
    wrapper: InjectableCss;
    wrapperMobile?: InjectableCss;
    warehouseSelect?: DynamicDropdownPresentationProps;
    warehouseSelectIcon?: IconPresentationProps;
}

const mapStateToProps = (state: CustomState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    const wareHouseList = state.customWareHouseList;
    const defaultWarehouse = state.customDefaultWarehouse.warehouse?.id || "";

    return {
        userIsAuthenticated,
        defaultWarehouse,
        wareHouseList,
    };
};

const mapDispatchToProps = {
    loadWarehouseList,
    loadDefaultWarehouse,
    changeCustomerContext,
    updateDefaultWarehouse,
};

export const warehouseSelectorStyles: WarehouseSelectorStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 0;
            position: relative;

            span.warehouse-menu-label {
                min-inline-size: max-content;
                margin: 0;
                padding-block-start: 2px;
            }
        `,
    },

    wrapperMobile: {
        css: css`
            background: ${getColor(colorStrings.commonGlobalWhite)};

            ul {
                display: flex;
                flex-direction: column;
                gap: 0;
                position: absolute;
                top: 6.5%;
            }

            li {
                border-top: 1px solid ${getColor(colorStrings.commonGlobalGray06)};
                text-align: left;
                color: ${getColor(colorStrings.textMain)};
                margin-top: -1px;
                padding: 8px 16px;
                position: relative;
                font-size: 16px;

                &:first-child {
                    border-top: none;
                }
            }

            li.mobile-row-wh--selected {
                color: ${getColor(colorStrings.commonGlobalWhite)};
                background: ${getColor(colorStrings.primaryMain)};

                span {
                    position: relative;
                    z-index: 2;
                }
            }
        `,
    },

    warehouseSelect: {
        mergeCss: true,
        iconProps: {
            src: undefined,
        },
        cssOverrides: {
            selectedText: css`
                inline-size: auto;
            `,
            option: css`
                ${commonStylesHeaderTypography}
                pointer-events: none;

                span {
                    pointer-events: auto;
                    display: block;
                    width: 100%;
                    height: 100%;
                }

                &[aria-selected="true"] {
                    background: ${getColor(colorStrings.secondaryMain)};
                }

                &:hover {
                    background: ${getColor(colorStrings.commonGlobalGray01)};
                    color: ${getColor(colorStrings.commonGlobalWhite)};
                }
            `,
            formInputWrapper: css`
                input:not(.react-datetime-picker__inputGroup__input) {
                    ${commonStylesHeaderTypography}
                    background-color: transparent !important;
                    border: none !important;
                    min-inline-size: 300px;
                }
            `,
        },
    },

    warehouseSelectIcon: {
        size: 20,
        css: css`
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const WarehouseSelector = ({
    userIsAuthenticated,
    defaultWarehouse,
    wareHouseList,
    isMobile,
    loadWarehouseList,
    loadDefaultWarehouse,
    updateDefaultWarehouse,
}: Props) => {
    const [selectedWH, setSelectedWH] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    const updateIconPosition = () => {
        const selectedText = document.querySelector(
            '[data-test-selector="productAddToListModal_listSelect-selectedText"]',
        );
        const label = document.querySelector(".warehouse-menu-label");
        if (selectedText && label) {
            const widthSelectedText = selectedText.getBoundingClientRect().width;
            const widthLabel = label.getBoundingClientRect().width;

            if (warehouseSelectorStyles.warehouseSelectIcon) {
                const width = widthLabel + widthSelectedText > 300 ? widthLabel + widthSelectedText : 300;
                warehouseSelectorStyles.warehouseSelectIcon.css = css`
                    position: absolute;
                    left: ${width - 20}px;
                    top: 50%;
                    transform: translateY(-50%);
                `;
            }

            if (warehouseSelectorStyles.warehouseSelect) {
                warehouseSelectorStyles.warehouseSelect = {
                    ...warehouseSelectorStyles.warehouseSelect,
                    cssOverrides: {
                        ...warehouseSelectorStyles.warehouseSelect.cssOverrides,
                        formInputWrapper: css`
                            input:not(.react-datetime-picker__inputGroup__input) {
                                ${commonStylesHeaderTypography}
                                background-color: transparent !important;
                                border: none !important;
                                min-inline-size: ${widthSelectedText > 300 ? widthSelectedText : 300}px;
                            }
                        `,
                    },
                };
            }
        }
    };

    useEffect(() => {
        // Run on component load
        updateIconPosition();

        window.addEventListener("resize", updateIconPosition);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("resize", updateIconPosition);
        };
    }, [isOpen, warehouseSelectorStyles, selectedWH]);

    const handleChange = useCallback(
        (e: any) => {
            setSelectedWH(e);
            setIsOpen(false);
            const selectedWarehouse = wareHouseList.warehouses.find(wh => wh.id === e);

            if (!selectedWarehouse) {
                return;
            }

            updateDefaultWarehouse({
                id: selectedWarehouse.id,
            });
        },
        [wareHouseList.warehouses],
    );

    useEffect(() => {
        if (userIsAuthenticated) {
            loadDefaultWarehouse({});
            loadWarehouseList({});
            updateIconPosition();
        }
    }, []);

    useEffect(() => {
        if (wareHouseList) {
            const { warehouses } = wareHouseList;

            setOptions(
                warehouses.map(wh => {
                    const searchString = `${wh.description}`;
                    return {
                        optionValue: wh.id,
                        optionText: searchString,
                        searchString,
                        rowChildren: <span className="warehouse-item">{searchString}</span>,
                        disabled: false,
                    };
                }),
            );
        }

        if (defaultWarehouse) {
            setSelectedWH(defaultWarehouse);
            updateIconPosition();
        }
    }, [wareHouseList, defaultWarehouse]);

    const toggleNavigation = () => {
        setIsOpen(!isOpen);
    };

    if (isMobile) {
        return (
            <StyledWrapper {...warehouseSelectorStyles.wrapperMobile}>
                <ul>
                    {options.map(option => (
                        <li
                            className={`${
                                selectedWH === option.optionValue ? "mobile-row-wh--selected" : "mobile-row-wh"
                            }`}
                            key={option.optionValue}
                            onClick={() => handleChange(option.optionValue)}
                        >
                            {option.rowChildren}
                        </li>
                    ))}
                </ul>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...warehouseSelectorStyles.wrapper}>
            <span className="warehouse-menu-label">{translate("ISC-Warehouse")}:</span>
            <Icon {...warehouseSelectorStyles.warehouseSelectIcon} src={isOpen ? ChevronUp : ChevronDown} />
            <DynamicDropdown
                {...warehouseSelectorStyles.warehouseSelect}
                isLoading={wareHouseList.isLoading}
                onSelectionChange={handleChange}
                selected={selectedWH}
                options={options}
                error={wareHouseList.errorMessage}
                data-test-selector="warehouse_listSelect"
                onOpen={toggleNavigation}
                onClose={toggleNavigation}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseSelector);
